import { format } from 'date-fns'
import { DateFormat } from '../enums'

/**
 * An instant (createdAt, validatedDate, scouting time…).
 *
 * Wire format: ISO 8601 with an offset (`2026-07-20T14:30:00+02:00`). In code, a `Date` instant. For a calendar day
 * with no time, use the date-only conversions instead.
 */
export type DateTime = string

/* eslint-disable max-len */
const ISO8601: RegExp =
  /^([\+-]?\d{4}(?!\d{2}\b))((-?)((0[1-9]|1[0-2])(\3([12]\d|0[1-9]|3[01]))?|W([0-4]\d|5[0-2])(-?[1-7])?|(00[1-9]|0[1-9]\d|[12]\d{2}|3([0-5]\d|6[1-6])))([T\s]((([01]\d|2[0-3])((:?)[0-5]\d)?|24\:?00)([\.,]\d+(?!:))?)?(\17[0-5]\d([\.,]\d+)?)?([zZ]|([\+-])([01]\d|2[0-3]):?([0-5]\d)?)?)?)?$/
/* eslint-enable max-len */

/** Whether the value is an ISO 8601 date or date-time string. */
export function isDateTime(value: string): boolean {
  return ISO8601.test(value)
}

/** An instant as ISO 8601 in the local wall time with its offset, e.g. `2026-07-20T14:30:00+02:00`. */
export function toDateTime(date: Date = new Date()): DateTime {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    throw new Error(`toDateTime: expected a valid date, got ${date}`)
  }
  return format(date, DateFormat.DATETIME_OFFSET)
}

/**
 * An instant as ISO 8601 in UTC, e.g. `2026-07-20T12:30:00.000Z`: what the API returns for the instants it reads from
 * the database. Never for a date-only column, whose value the driver reads at local midnight: use `toDateOnly`.
 */
export function toUtcDateTime(date: Date): DateTime
export function toUtcDateTime(date: Date | null | undefined): DateTime | null
export function toUtcDateTime(date: Date | null | undefined): DateTime | null {
  return date ? date.toISOString() : null
}

/** The instant of an ISO 8601 string, or `undefined` when the value is empty or not ISO 8601. */
export function parseDateTime(value: string | null | undefined): Date | undefined {
  if (!value || !isDateTime(value)) {
    return undefined
  }
  return new Date(Date.parse(value))
}
